import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from "react";

import "./loom-panel-layout.css";

const MIN_LEFT = 190;
const MAX_LEFT = 420;
const MIN_RIGHT = 210;
const MAX_RIGHT = 440;
const DEFAULT_LEFT = 270;
const DEFAULT_RIGHT = 270;

interface StoredLayout {
  leftWidth: number;
  rightWidth: number;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function initialLayout(storageKey: string): StoredLayout {
  if (typeof window === "undefined") return { leftWidth: DEFAULT_LEFT, rightWidth: DEFAULT_RIGHT };
  try {
    const value = JSON.parse(window.localStorage.getItem(storageKey) ?? "null") as Partial<StoredLayout> | null;
    return {
      leftWidth: clamp(Number(value?.leftWidth) || DEFAULT_LEFT, MIN_LEFT, MAX_LEFT),
      rightWidth: clamp(Number(value?.rightWidth) || DEFAULT_RIGHT, MIN_RIGHT, MAX_RIGHT),
    };
  } catch {
    return { leftWidth: DEFAULT_LEFT, rightWidth: DEFAULT_RIGHT };
  }
}

interface LoomPanelLayoutProps {
  activePane?: LoomWorkbenchPane | null;
  children: ReactNode;
  context?: ReactNode;
  onActivePaneChange?: (pane: LoomWorkbenchPane | null) => void;
  storageId: string;
  taskDriven?: boolean;
  viewportFirst?: boolean;
}

export type LoomWorkbenchPane = "pattern" | "inspect" | "truth" | "activity";

const PANE_ITEMS: readonly {
  controls: string;
  glyph: string;
  label: string;
  pane: LoomWorkbenchPane;
  side: "left" | "right";
}[] = [
  { controls: "loom-pattern-pane", glyph: "⬡", label: "Pattern", pane: "pattern", side: "left" },
  { controls: "loom-inspector-pane", glyph: "▦", label: "Inspect", pane: "inspect", side: "right" },
  { controls: "loom-truth-pane", glyph: "◆", label: "Truth", pane: "truth", side: "right" },
  { controls: "loom-activity-pane", glyph: "◷", label: "Activity", pane: "activity", side: "right" },
];

export function LoomPanelLayout({
  activePane,
  children,
  context,
  onActivePaneChange,
  storageId,
  taskDriven = false,
  viewportFirst = false,
}: LoomPanelLayoutProps) {
  const storageKey = `thread:loom:panel-layout:${storageId}`;
  const [layout, setLayout] = useState(() => initialLayout(storageKey));
  const [stageMaximized, setStageMaximized] = useState(false);
  const [localPane, setLocalPane] = useState<LoomWorkbenchPane | null>(null);
  const [compactRail, setCompactRail] = useState(false);
  const dragCleanupRef = useRef<(() => void) | null>(null);
  const paneButtonRefs = useRef<Partial<Record<LoomWorkbenchPane, HTMLButtonElement | null>>>({});
  const restorePaneFocusRef = useRef<LoomWorkbenchPane | null>(null);
  const currentPane = activePane === undefined ? localPane : activePane;

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(layout));
    } catch {
      // Layout preferences are best-effort; authoring remains available when storage is denied.
    }
  }, [layout, storageKey]);

  useEffect(() => () => dragCleanupRef.current?.(), []);

  useEffect(() => {
    if (!viewportFirst || typeof window.matchMedia !== "function") return;
    const media = window.matchMedia("(max-width: 760px)");
    const update = () => setCompactRail(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [viewportFirst]);

  useEffect(() => {
    if (currentPane !== null || restorePaneFocusRef.current === null) return;
    const pane = restorePaneFocusRef.current;
    restorePaneFocusRef.current = null;
    paneButtonRefs.current[pane]?.focus();
  }, [currentPane]);

  useEffect(() => {
    if (!stageMaximized && (!viewportFirst || currentPane === null)) return;
    const restore = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (stageMaximized) {
        setStageMaximized(false);
        return;
      }
      restorePaneFocusRef.current = currentPane;
      if (activePane === undefined) setLocalPane(null);
      onActivePaneChange?.(null);
    };
    window.addEventListener("keydown", restore);
    return () => window.removeEventListener("keydown", restore);
  }, [activePane, currentPane, onActivePaneChange, stageMaximized, viewportFirst]);

  const resizeBy = (side: "left" | "right", delta: number) => {
    setLayout((current) => side === "left"
      ? { ...current, leftWidth: clamp(current.leftWidth + delta, MIN_LEFT, MAX_LEFT) }
      : { ...current, rightWidth: clamp(current.rightWidth + delta, MIN_RIGHT, MAX_RIGHT) });
  };

  const beginResize = (side: "left" | "right", event: PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    dragCleanupRef.current?.();
    const startX = event.clientX;
    const startWidth = side === "left" ? layout.leftWidth : layout.rightWidth;
    const move = (moveEvent: globalThis.PointerEvent) => {
      const signedDelta = side === "left" ? moveEvent.clientX - startX : startX - moveEvent.clientX;
      const next = startWidth + signedDelta;
      setLayout((current) => side === "left"
        ? { ...current, leftWidth: clamp(next, MIN_LEFT, MAX_LEFT) }
        : { ...current, rightWidth: clamp(next, MIN_RIGHT, MAX_RIGHT) });
    };
    const cleanup = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", cleanup);
      dragCleanupRef.current = null;
    };
    dragCleanupRef.current = cleanup;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", cleanup);
  };

  const resizeWithKeyboard = (side: "left" | "right", event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const direction = event.key === "ArrowRight" ? 1 : -1;
    resizeBy(side, side === "left" ? direction * 16 : direction * -16);
  };

  const style = {
    "--pg-left-width": `${layout.leftWidth}px`,
    "--pg-right-width": `${layout.rightWidth}px`,
  } as CSSProperties;

  const selectPane = (pane: LoomWorkbenchPane) => {
    const next = currentPane === pane ? null : pane;
    if (activePane === undefined) setLocalPane(next);
    onActivePaneChange?.(next);
  };

  const rail = (side: "left" | "right") => (
    <div
      aria-label={`${side === "left" ? "Left" : "Right"} dock`}
      aria-orientation={compactRail ? "horizontal" : "vertical"}
      className={`loom-pane-rail is-${side}`}
      data-testid={`loom-${side}-pane-rail`}
      role="toolbar"
    >
      {PANE_ITEMS.filter((item) => item.side === side).map((item) => {
        const open = currentPane === item.pane && !stageMaximized;
        return (
          <button
            aria-controls={item.controls}
            aria-expanded={open}
            aria-label={`${item.label} pane`}
            aria-pressed={open}
            data-testid={`loom-pane-toggle-${item.pane === "inspect" ? "inspector" : item.pane}`}
            key={item.pane}
            onClick={() => selectPane(item.pane)}
            ref={(node) => { paneButtonRefs.current[item.pane] = node; }}
            title={`${open ? "Close" : "Open"} ${item.label} pane`}
            type="button"
          >
            <span aria-hidden="true" className="loom-pane-rail-glyph">{item.glyph}</span>
            <span className="loom-pane-rail-label">{item.label}</span>
            <i aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );

  return (
    <div
      className={`pg-layout${viewportFirst ? " is-viewport-first" : ""}${taskDriven ? " is-task-driven" : ""}${stageMaximized ? " is-stage-maximized" : ""}`}
      data-active-pane={currentPane ?? "closed"}
      data-stage-maximized={String(stageMaximized)}
      data-testid="loom-panel-layout"
      style={style}
    >
      {viewportFirst && !taskDriven ? rail("left") : null}
      {viewportFirst && !taskDriven ? rail("right") : null}
      {viewportFirst && !taskDriven && context ? <div className="loom-compact-context" data-testid="loom-workbench-context">{context}</div> : null}
      {children}
      {!taskDriven ? <button
        aria-valuemax={MAX_LEFT}
        aria-valuemin={MIN_LEFT}
        aria-valuenow={layout.leftWidth}
        aria-label="Resize Loom authoring panel"
        aria-orientation="vertical"
        className="pg-resize-handle is-left"
        data-testid="loom-resize-left"
        onKeyDown={(event) => resizeWithKeyboard("left", event)}
        onPointerDown={(event) => beginResize("left", event)}
        role="separator"
        type="button"
      /> : null}
      {!taskDriven ? <button
        aria-valuemax={MAX_RIGHT}
        aria-valuemin={MIN_RIGHT}
        aria-valuenow={layout.rightWidth}
        aria-label="Resize Loom evidence panel"
        aria-orientation="vertical"
        className="pg-resize-handle is-right"
        data-testid="loom-resize-right"
        onKeyDown={(event) => resizeWithKeyboard("right", event)}
        onPointerDown={(event) => beginResize("right", event)}
        role="separator"
        type="button"
      /> : null}
      {!taskDriven ? <button
        aria-label={stageMaximized ? "Restore Loom panels" : "Maximize Loom viewport"}
        aria-pressed={stageMaximized}
        className="pg-stage-maximize"
        data-testid="loom-stage-maximize"
        onClick={() => setStageMaximized((current) => !current)}
        title={stageMaximized ? "Restore panels (Esc)" : "Maximize viewport"}
        type="button"
      >
        <span aria-hidden="true">{stageMaximized ? "↙" : "↗"}</span>
      </button> : null}
    </div>
  );
}
